import React, { Children, cloneElement } from 'react';
import { Navigate } from 'react-router-dom';

import { useURLQuery } from '../../Common/Hook/urlQuery';
import {
  useApplicationsBulkUpdatePutQuery,
  useApplicationsBulkUploadPostQuery,
} from '../../Common/Queries/applicationsQueries';
import {
  useInterviewsGetQuery,
  useThresholdUpdateQueryById,
} from '../../Common/Queries/interviewsQueries';
import {
  usePositionsGetQuery,
  usePositionsKanbanGetQueryById,
} from '../../Common/Queries/positionsQueries';

import { htmlToMD, isLogin, isValidID } from '../../utils';
import { getAllInterviewMap } from '../../utils/responseMap.ts';

import { ALL_DATA_LIMIT, DURATION_TIME, MY_OPEN_POSITIONS_ENDPOINT } from '../../constants';

export const convertType = (type, value) => {
  if (['number', 'linear scale', 'rating'].includes(type)) {
    return Number(value);
  }
  if (type === 'check box') {
    return value.split(';');
  }
  return value;
};

export const formateData = (values) => {
  return {
    reason: htmlToMD(values?.reason),
    id: values?.id,
    name: values?.name,
    position: values?.position,
    agencyId: values?.agencyId,
    email: values?.email,
    phone: values.phone,
    status: values?.status,
    updatedStatus: values?.updatedStatus,
    createdAt: values?.createdAt,
    updatedAt: values?.updatedAt,
    versionKey: values?.versionKey,
    allowedOperations: values?.allowedOperations,
    events: values?.events,
    interview: values?.interview,
    coolingPeriod: values?.coolingPeriod,
    referrer: values?.referrer,
    uploadedBy: values?.uploadedBy,
    recruiter: values?.recruiter,
    agency: values?.agency,
    lastRoundInfo: values?.lastRoundInfo,
    requiredInfo: values?.requiredInfo,
    applicantID: values?.applicantID,
    isUpload: values?.isUpload,
    isReferral: values?.isReferral,
    isCareer: values?.isCareer,
    latestRoundID: values?.latestRoundID,
    orgID: values?.orgID,
    customFormData: values?.customFormData,
    invalidFields: values?.invalidFields,
  };
};

export const formatData = (value, order = []) => {
  return {
    name: value?.name,
    phone: `+91${value?.phone}`,
    email: value?.email,
    position: value?.position,
    isUpload: false,
    customFormData: [
      ...(function customFormData() {
        const result = Object.entries(value)
          ?.filter((v) => !['email', 'phone', 'name', 'position'].includes(v[0]))
          ?.reduce((initialiValue, obj) => {
            return { [obj[0]]: obj[1], ...initialiValue };
          }, {});
        const resultArr = order?.map((obj) => {
          return {
            fieldName: obj?.fieldName,
            ...(result[obj?.fieldName?.toLowerCase()] && {
              fieldValue: convertType(
                obj?.fieldType?.toLowerCase(),
                result[obj?.fieldName?.toLowerCase()]
              ),
            }),
          };
        });
        return resultArr;
      })(),
    ],
  };
};

export function ApplicationStatsContainer({ children }) {
  const child = Children.only(children);
  const urlQuery = useURLQuery();
  const positionID = isValidID(urlQuery.get('positionID')) && urlQuery.get('positionID');

  // If positionID is not present redirecting to My Open Positions page
  if (!positionID) {
    return <Navigate to={MY_OPEN_POSITIONS_ENDPOINT} />;
  }

  const applications = usePositionsKanbanGetQueryById({ reqId: positionID });
  const putToApplication = useApplicationsBulkUpdatePutQuery();
  const updateApplication = async (values) => {
    const reqBody = formateData(values);
    const reqParams = { reqBody, reqId: values.id };
    await putToApplication.mutateAsync(reqParams);
  };
  const putToInterview = useThresholdUpdateQueryById();

  const interviews = useInterviewsGetQuery(
    { positionID },
    { select: (data) => getAllInterviewMap(data) }
  );

  const postApplications = useApplicationsBulkUploadPostQuery();
  const postBulkApplications = async (reqBody, order) => {
    const formatedReqBody = formatData(reqBody, order);
    const data = await postApplications.mutateAsync(formatedReqBody);
    return { status: 'success', data };
  };

  const positionList = usePositionsGetQuery(
    { limit: ALL_DATA_LIMIT },
    { enabled: isLogin(), refetchInterval: DURATION_TIME, refetchIntervalInBackground: true }
  );

  return cloneElement(child, {
    applications,
    positionID,
    updateApplication,
    putToInterview,
    interviews,
    postBulkApplications,
    positionList,
  });
}
